import React, { useEffect, useMemo, useState } from "react";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import { FaExternalLinkAlt, FaMapMarkerAlt, FaPlus, FaTrash, FaVideo } from "react-icons/fa";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import { PermissionEnum } from "../../../../../utils/enums/permission-enum.js";
import { navigateHook } from "../../../../../hooks/use-navigate.js";
import ModalCameras from "./modal-cameras.jsx";
import ModalSectors from "./modal-sectors.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import {Card} from "../../../../../components/card/card.jsx";
import {useQuery} from "@tanstack/react-query";
import vmsService from "../../../../../services/web/vms-service.js";

const AlarmCameras = ({ cameras, setCameras, sectors }) => {
    const [lines, setLines] = useState("");
    const [columns, setColumns] = useState("");

    const [appliedLines, setAppliedLines] = useState(null);
    const [appliedColumns, setAppliedColumns] = useState(null);

    const [selectedCameras, setSelectedCameras] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState(null);

    const [openModalCameras, setOpenModalCameras] = useState(false);
    const [openModalSectors, setOpenModalSectors] = useState(false);

    const hasPermission = useHasPermission();
    const navigate = navigateHook();

    const { data: camerasData = [], isLoading } = useQuery({
        queryKey: ["vms-cameras"],
        queryFn: () => vmsService.list(),
    });

    const {data: vmsData} = useQuery({
        queryFn: () => vmsService.getAll(),
        queryKey: ["vms-recorders-all"]
    });

    useEffect(() => {
        if (!cameras) return;

        setLines(cameras.quantityLines ?? "");
        setColumns(cameras.quantityColumns ?? "");

        setAppliedLines(cameras.quantityLines ?? "");
        setAppliedColumns(cameras.quantityColumns ?? "");

        setSelectedCameras(
            Array.isArray(cameras.vmsCameras)
                ? cameras.vmsCameras
                : []
        );
    }, [cameras]);

    const apply = () => {
        if (!lines || !columns) {
            projectUtils.swalErrorMessage("", "Preencha os campos obrigatórios.");
            return;
        }

        const payload = {
            quantityLines: Number(lines),
            quantityColumns: Number(columns),
            vmsCameras: selectedCameras
        };

        setAppliedLines(payload.quantityLines);
        setAppliedColumns(payload.quantityColumns);

        setCameras(payload);
    };

    const addCamera = (position) => {
        setSelectedPosition(position);
        setOpenModalCameras(true);
    };

    const removeCamera = (position) => {
        const updated = selectedCameras.filter(
            cam => cam.position !== position
        );

        setSelectedCameras(updated);
        setCameras({ ...cameras, vmsCameras: updated });
    };

    const handleSelectCamera = (camera) => {
        const normalizedCamera = {
            id: camera.id,
            name: camera.cameraName ?? camera.name ?? "",
            cameraName: camera.cameraName ?? camera.name ?? "",
            dvrName: camera.dvrName,
            vmsId: camera.vmsId,
            vmsName: camera.vmsName,
            labels: camera.labels ?? [],
            sectors: [],
            position: selectedPosition
        };

        const updated = selectedCameras.filter(
            cam =>
                cam.position !== selectedPosition &&
                cam.id !== camera.id
        );

        updated.push(normalizedCamera);

        setSelectedCameras(updated);
        setCameras({
            ...cameras,
            vmsCameras: updated
        });

        setOpenModalCameras(false);
    };


    const handleSelectSectors = (sectorIds) => {
        const updated = selectedCameras.map(cam =>
            cam.position === selectedPosition
                ? { ...cam, sectors: sectorIds }
                : cam
        );

        setSelectedCameras(updated);
        setCameras({ ...cameras, vmsCameras: updated });
        setOpenModalSectors(false);
    };

    const matrix = useMemo(() => {
        if (!appliedLines || !appliedColumns) {
            return [];
        }

        const result = [];
        let position = 1;

        for (let i = 0; i < appliedLines; i++) {
            const row = [];

            for (let j = 0; j < appliedColumns; j++) {
                const camera =
                    selectedCameras.find(
                        cam => cam.position === position
                    ) || null;

                row.push({ position, camera });
                position++;
            }

            result.push(row);
        }

        return result;
    }, [appliedLines, appliedColumns, selectedCameras]);

    const selectionRule = useMemo(() => {
        if (!selectedCameras.length) return null;

        const first = selectedCameras[0];

        return {
            connection: first?.connectionType || null,
            dvrName: first?.dvrName || null
        };
    }, [selectedCameras]);

    return (
        <>
            <div className="mt-4">
                <h4 className="font-semibold mb-3 border-b border-gray-200">
                    Associação de câmeras de vídeo
                </h4>

                <div className={"w-full h-full flex justify-between items-start gap-2"}>
                    <div className={"flex flex-col gap-2 w-1/6 h-full"}>
                        <Card className={"flex flex-col gap-2 p-2 pt-4"}>
                            <span className={"font-medium absolute -top-2.5 bg-white px-2"}>Matriz</span>

                            <InputForm
                                id="lines"
                                label="Linhas *"
                                type="number"
                                value={lines}
                                onChange={e => setLines(Number(e.target.value))}
                                max={6}
                                classNameWrapper="w-full"
                            />

                            <InputForm
                                id="columns"
                                label="Colunas *"
                                type="number"
                                value={columns}
                                onChange={e => setColumns(Number(e.target.value))}
                                max={6}
                                classNameWrapper="w-full"
                            />
                        </Card>

                        <Button
                            className={"w-full"}
                            target={"primary"}

                            onClick={apply}
                        >
                            Aplicar
                        </Button>

                        { hasPermission(PermissionEnum.SOS_MANAGE_ASSET) && (
                            <div
                                className="w-full flex justify-center items-center gap-2 text-blue-900 hover:text-blue-600 cursor-pointer"
                                target="default"
                                onClick={() => navigate("/video-cameras", true)}
                            >
                                <FaExternalLinkAlt /> Adicionar câmera
                            </div>
                        )}
                    </div>

                    <div className={"w-5/6 border-l border-gray-100 pl-2"}>
                        { lines && columns && matrix.length > 0 && (
                            <div
                                className="grid gap-2 w-full"
                                style={{
                                    gridTemplateColumns: `repeat(${appliedColumns}, minmax(0, 1fr))`
                                }}
                            >
                                { matrix.flat().map(cell => (
                                    <div
                                        key={cell.position}
                                        className="border border-gray-200 rounded-md p-3 bg-gray-50 flex justify-center w-full min-h-26.25"
                                    >
                                        { cell.camera ? (
                                            <div className="w-full">
                                                <p className="text-sm line-clamp-2">
                                                    Nome: <b>{cell.camera.name}</b>
                                                </p>
                                                <p className="text-sm line-clamp-2">
                                                    VMS: <b>{
                                                    (() => {
                                                        const vms = vmsData?.find(v => v.id === cell.camera.vmsId);
                                                        return vms?.vmsCorporationName || vms?.vmsUrl || "-";
                                                    })()
                                                }</b>
                                                </p>
                                                <p className="text-sm line-clamp-2">
                                                    Setores: <b>{cell.camera?.sectors?.join(', ')}</b>
                                                </p>

                                                <div className="flex justify-center items-center gap-2 mt-2">
                                                    <Button title={"Editar câmera"} onClick={() => addCamera(cell.position)}>
                                                        <FaVideo />
                                                    </Button>

                                                    <Button title={"Editar setores"} onClick={() => {
                                                        setSelectedPosition(cell.position);
                                                        setOpenModalSectors(true);
                                                    }}>
                                                        <FaMapMarkerAlt />
                                                    </Button>

                                                    <Button target="danger" title={"Remover câmera"} onClick={() => removeCamera(cell.position)}>
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex justify-center items-center cursor-pointer w-full" onClick={() => addCamera(cell.position)}>
                                                <FaPlus className="text-4xl text-gray-300" />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ModalCameras
                open={openModalCameras}
                setOpen={setOpenModalCameras}
                vms={vmsData}
                cameras={camerasData}
                onSelectCamera={handleSelectCamera}
                selectedCameras={selectedCameras}
                selectionRule={selectionRule}
                isLoading={isLoading}
            />

            <ModalSectors
                open={openModalSectors}
                setOpen={setOpenModalSectors}
                sectors={sectors}
                selectedSectorIds={selectedCameras.find(c => c.position === selectedPosition)?.sectors || []}
                onSave={handleSelectSectors}
            />
        </>
    );
};

export default AlarmCameras;